import CircularProgress from '@mui/material/CircularProgress';
import React__default, { forwardRef, useState, useRef, useImperativeHandle, useEffect } from 'react';
import { TextField, Label, TextArea as TextArea$1, FieldError } from 'react-aria-components';
import { WarningIcon } from '../icons/WarningIcon.js';
import './TextArea.scss';

const TextArea = forwardRef(({ variant = 'outline', rounding = '10', size = 'large', name, className, ariaLabel, label, placeholder, hasError, isDisabled, isReadOnly, isRequired, isOptional, isLoading, rows = 3, maxLength, showCharCount, autoResize, ...props }, ref) => {
    const { value: controlledValue, defaultValue, onChange, onFocus, onBlur, ...restProps } = props;
    const isControlled = controlledValue !== undefined;
    const [internalValue, setInternalValue] = useState(controlledValue ?? defaultValue ?? '');
    const internalRef = useRef(null);
    useImperativeHandle(ref, () => internalRef.current);
    // Keep internal value in sync when controlled
    useEffect(() => {
        if (isControlled && controlledValue !== undefined) {
            setInternalValue(controlledValue);
        }
    }, [controlledValue, isControlled]);
    // Auto-resize: keep min-height from CSS (rows), but grow to fit content
    useEffect(() => {
        if (!autoResize || !internalRef.current)
            return;
        const el = internalRef.current;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
        el.style.overflow = 'hidden';
    }, [internalValue, autoResize, rows]);
    // When autoResize is off, restore default overflow/height behavior
    useEffect(() => {
        if (autoResize || !internalRef.current)
            return;
        const el = internalRef.current;
        el.style.overflow = 'auto';
        el.style.height = '';
    }, [autoResize, rows]);
    const handleChange = (value) => {
        if (isDisabled || isReadOnly)
            return;
        if (!isControlled) {
            setInternalValue(value);
        }
        onChange?.(value);
    };
    const handleFocus = () => {
        onFocus?.(internalValue);
    };
    const handleBlur = () => {
        onBlur?.(internalValue);
    };
    const characterCount = internalValue.length;
    return (React__default.createElement(TextField, { "data-rev-component": 'text-area', "data-rev-isloading": isLoading, name: name, className: className, "aria-label": ariaLabel, isDisabled: isDisabled, isInvalid: !!hasError, isReadOnly: isReadOnly, isRequired: isRequired, value: internalValue, 
        // Only pass defaultValue when uncontrolled to avoid React warnings
        defaultValue: isControlled ? undefined : defaultValue, onChange: handleChange, ...restProps },
        label && (React__default.createElement(Label, { className: 'text-area-label--rev', "data-invalid": !!hasError },
            label,
            isRequired && (React__default.createElement("span", { className: 'field-label-required--rev' }, "(required)")),
            isOptional && (React__default.createElement("span", { className: 'field-label-optional--rev' }, "(optional)")))),
        React__default.createElement("div", { className: 'text-area-group--rev', "data-rev-variant": variant, "data-rev-size": size, "data-rev-isloading": isLoading, "data-rev-rounding": rounding, "data-disabled": isDisabled || isReadOnly, "data-invalid": !!hasError },
            React__default.createElement(TextArea$1, { ref: internalRef, className: 'text-area-input--rev', "aria-invalid": !!hasError, placeholder: placeholder, rows: rows, maxLength: maxLength, disabled: isDisabled, readOnly: isReadOnly, 
                // expose rows to CSS so min-height can use it
                style: {
                    '--rev-textarea-rows': rows,
                }, onFocus: handleFocus, onBlur: handleBlur }),
            isLoading && (React__default.createElement("div", { className: 'text-area-side--rev spinner--rev' },
                React__default.createElement(CircularProgress, { color: 'inherit' })))),
        showCharCount && maxLength && (React__default.createElement("div", { className: 'text-area-charcount--rev' },
            characterCount,
            "/",
            maxLength)),
        hasError && typeof hasError === 'string' && (React__default.createElement(FieldError, { className: 'text-area-error--rev' },
            React__default.createElement(WarningIcon, null),
            hasError))));
});
TextArea.displayName = 'TextArea';

export { TextArea };
//# sourceMappingURL=TextArea.js.map
